export type InternalParkingSpotOccupancy = 0 | 1;

export type InternalParkingSpot = {
  id: string;
  label: string;
  areaName: string;
  slotNumber: string;
  occupancy: InternalParkingSpotOccupancy | null;
};

export type InternalParkingAreaSnapshot = {
  areaName: string;
  spots: InternalParkingSpot[];
  total: number;
  occupied: number;
};

export type InternalParkingSnapshot = {
  areas: InternalParkingAreaSnapshot[];
  spotOccupancy: Record<string, InternalParkingSpotOccupancy>;
  occupied: number | null;
  total: number | null;
  free: number | null;
  timestamp: string | null;
};

const MAX_DEPTH = 6;

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized.length) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const tryParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const resolveMaybeJson = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.length) {
    return null;
  }
  return tryParseJson(trimmed) ?? value;
};

const normalizeText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const normalizeSpotIdentifierPart = (value: string) => value.trim().replace(/\s+/g, '');

const buildSpotId = (areaName: string, slotNumber: string) =>
  `${normalizeSpotIdentifierPart(areaName)}${normalizeSpotIdentifierPart(slotNumber)}`;

const parseOccupancy = (value: unknown): InternalParkingSpotOccupancy | null => {
  const parsed = parseNumber(value);
  if (parsed === null) {
    return null;
  }
  return parsed > 0 ? 1 : 0;
};

const normalizeAreaName = (value: unknown, fallback: string): string => {
  const normalized = normalizeText(value);
  return normalized ?? fallback;
};

const resolveSlotNumbers = (value: unknown, occupancyLength: number): string[] => {
  const resolved = resolveMaybeJson(value);
  if (Array.isArray(resolved)) {
    const numbers = resolved
      .map((entry) => normalizeText(entry))
      .filter((entry): entry is string => Boolean(entry));
    if (numbers.length) {
      return numbers;
    }
  }
  return Array.from({ length: occupancyLength }, (_, index) => String(index + 1));
};

const resolveOccupancyEntries = (value: unknown): unknown[] => {
  const resolved = resolveMaybeJson(value);
  return Array.isArray(resolved) ? resolved : [];
};

const parseInternalParkingArea = (value: unknown, index: number): InternalParkingAreaSnapshot | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const areaName = normalizeAreaName(record.area_name ?? record.areaName ?? record.name, `Strefa ${index + 1}`);
  const occupancyEntries = resolveOccupancyEntries(record.occupancy);
  if (!occupancyEntries.length) {
    return null;
  }

  const slotNumbers = resolveSlotNumbers(record.numbering_scheme ?? record.numberingScheme ?? record.slots, occupancyEntries.length);
  const spots: InternalParkingSpot[] = [];

  occupancyEntries.forEach((entry, occupancyIndex) => {
    const slotNumber = slotNumbers[occupancyIndex] ?? String(occupancyIndex + 1);
    const occupancy = parseOccupancy(entry);
    const id = buildSpotId(areaName, slotNumber);
    spots.push({
      id,
      label: id,
      areaName,
      slotNumber,
      occupancy,
    });
  });

  return {
    areaName,
    spots,
    total: spots.length,
    occupied: spots.reduce((count, spot) => count + (spot.occupancy === 1 ? 1 : 0), 0),
  };
};

const resolveParkingDetailArray = (value: unknown): unknown[] => {
  const resolved = resolveMaybeJson(value);
  return Array.isArray(resolved) ? resolved : [];
};

const hasInternalParkingShape = (record: Record<string, unknown>): boolean =>
  'parking_detail' in record || 'parkingDetail' in record || 'detail' in record;

const extractInternalParkingSource = (payload: unknown, depth = 0): Record<string, unknown> | null => {
  if (depth > MAX_DEPTH || payload === null || typeof payload === 'undefined') {
    return null;
  }

  const resolved = resolveMaybeJson(payload);
  if (resolved === null || typeof resolved === 'undefined') {
    return null;
  }

  if (Array.isArray(resolved)) {
    for (const entry of resolved) {
      const extracted = extractInternalParkingSource(entry, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }

  if (typeof resolved !== 'object') {
    return null;
  }

  const record = resolved as Record<string, unknown>;
  if (hasInternalParkingShape(record)) {
    return record;
  }

  const candidates = [record.results, record.result, record._data_, record.data, record.response, record.payload, record.body];
  for (const candidate of candidates) {
    if (!candidate || candidate === resolved) {
      continue;
    }
    const extracted = extractInternalParkingSource(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  for (const candidate of Object.values(record)) {
    if (!candidate || candidate === resolved) {
      continue;
    }
    const extracted = extractInternalParkingSource(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  return null;
};

export const normalizeInternalParkingAreaKey = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

export const flattenInternalParkingSpots = (snapshot: InternalParkingSnapshot | null | undefined): InternalParkingSpot[] =>
  snapshot?.areas.flatMap((area) => area.spots.map((spot) => ({ ...spot }))) ?? [];

export const extractInternalParkingSnapshot = (payload: unknown): InternalParkingSnapshot | null => {
  const source = extractInternalParkingSource(payload);
  if (!source) {
    return null;
  }

  const detailEntries = resolveParkingDetailArray(source.parking_detail ?? source.parkingDetail ?? source.detail);
  const areas = detailEntries
    .map((entry, index) => parseInternalParkingArea(entry, index))
    .filter((entry): entry is InternalParkingAreaSnapshot => Boolean(entry));

  const spotOccupancy = areas.reduce<Record<string, InternalParkingSpotOccupancy>>((accumulator, area) => {
    area.spots.forEach((spot) => {
      if (spot.occupancy === null) {
        return;
      }
      accumulator[spot.id] = spot.occupancy;
    });
    return accumulator;
  }, {});

  const occupiedFromAreas = areas.reduce((sum, area) => sum + area.occupied, 0);
  const totalFromAreas = areas.reduce((sum, area) => sum + area.total, 0);

  const occupiedFromPayload = parseNumber(source.total_occupied ?? source.totalOccupied ?? source.occupied);
  const freeFromPayload = parseNumber(source.total_available ?? source.totalAvailable ?? source.free);
  const occupied = occupiedFromPayload ?? (totalFromAreas > 0 ? occupiedFromAreas : null);
  const total = freeFromPayload !== null ? (occupied ?? 0) + freeFromPayload : totalFromAreas > 0 ? totalFromAreas : occupied;
  const free = total !== null && occupied !== null ? Math.max(total - occupied, 0) : freeFromPayload;
  const timestamp = normalizeText(source.time ?? source.ctime ?? source.timestamp);

  if (!areas.length && occupied === null && total === null) {
    return null;
  }

  return {
    areas,
    spotOccupancy,
    occupied,
    total,
    free,
    timestamp,
  };
};
